import { useEffect, useRef } from 'react';
import { springs, useMotion } from '../motion';

// Centered dialog with a dimmed backdrop; closes on backdrop click and Escape
// wide: for dialogs with more content (server settings)
export default function Modal({ title, onClose, children, footer, wide = false }) {
  const root = useRef();
  // Phones: a sheet sliding up from the bottom. Larger screens: a card that springs open.
  const sheet = typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches;
  useMotion(root, {
    parts: {
      '[data-backdrop]': { enter: { opacity: 0 }, exit: { opacity: 0 } },
      '[role="dialog"]': sheet
        ? { enter: { y: '100%' }, exit: { y: '100%' } }
        : { enter: { y: 24, scale: 0.94, opacity: 0 }, exit: { scale: 0.96, opacity: 0 } },
    },
    config: springs.sheet,
  });
  useEffect(() => {
    const key = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  }, [onClose]);

  return (
    <div
      ref={root}
      className="fixed inset-0 z-[600] flex items-end justify-center p-0 sm:items-center sm:p-4"
      onMouseDown={onClose}
      data-noswipe
    >
      <div data-backdrop className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.65)' }} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={e => e.stopPropagation()}
        className={`relative w-full ${wide ? 'max-w-[600px]' : 'max-w-[420px]'} rounded-t-xl border border-solid border-border bg-surface-2 pb-[var(--safe-bottom)] shadow-modal sm:rounded-xl sm:pb-0`}
      >
        <div className="flex items-center justify-between px-5 pb-2 pt-4">
          <h2 className="m-0 font-display text-lg font-extrabold text-text">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent text-text-muted hover:bg-surface-3 hover:text-text"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>
        <div className="px-5 pb-4">{children}</div>
        {footer && <div className="flex gap-2 border-0 border-t border-solid border-border px-5 py-3">{footer}</div>}
      </div>
    </div>
  );
}

export function ModalButton({ primary, danger, disabled, onClick, children }) {
  const tone = primary
    ? 'border-0 bg-brand text-white shadow-button'
    : danger
      ? 'border-0 bg-red text-white'
      : 'border border-solid border-border bg-transparent text-text-muted hover:text-text';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`flex-1 rounded-pill py-2.5 font-display text-sm font-bold ${tone} ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
    >
      {children}
    </button>
  );
}
